import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import DocumentScanner from '../components/DocumentScanner';

/**
 * App-level receipt capture. The scanner can be opened from anywhere (the
 * bottom-nav ＋ sheet, the expense form) and the captured file is handed to
 * the expense form via takePendingCapture().
 */
const CaptureContext = createContext(null);

export function CaptureProvider({ children }) {
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(false);
  const pendingRef = useRef(null);
  // When set, the scanner result goes to this callback instead of navigating
  const onCaptureRef = useRef(null);

  const handOff = useCallback((file, previewUrl, { replace = false } = {}) => {
    pendingRef.current = { file, previewUrl };
    navigate('/expenses/new', { replace });
  }, [navigate]);

  const openScanner = useCallback((onCapture = null) => {
    onCaptureRef.current = onCapture;
    setScannerOpen(true);
  }, []);

  // Start a new expense from a file picked from photos/files
  const startFromFile = useCallback((file) => {
    handOff(file, URL.createObjectURL(file));
  }, [handOff]);

  const takePendingCapture = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    return pending;
  }, []);

  const handleCapture = (file, previewUrl) => {
    setScannerOpen(false);
    const cb = onCaptureRef.current;
    onCaptureRef.current = null;
    if (cb) cb(file, previewUrl);
    // The scanner pushed a history entry so "back" closes it — replace that
    // entry so back from the form returns to where the capture started
    else handOff(file, previewUrl, { replace: true });
  };

  return (
    <CaptureContext.Provider value={{ openScanner, startFromFile, takePendingCapture }}>
      {children}
      {scannerOpen && createPortal(
        <DocumentScanner
          onCapture={handleCapture}
          onClose={() => { onCaptureRef.current = null; setScannerOpen(false); }}
        />,
        document.body
      )}
    </CaptureContext.Provider>
  );
}

export function useCapture() {
  const ctx = useContext(CaptureContext);
  if (!ctx) throw new Error('useCapture must be used within CaptureProvider');
  return ctx;
}
